import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Navbar,
  Button,
  Input,
  LoadingSpinner,
  ErrorState,
  NoApplicationsState,
} from '../components';
import { applicationService, eventService } from '../services';
import { useUser, useAuth } from '../context';

/**
 * Guest Dashboard Page - Find Your Party
 * Three-column layout: Your Events (2 cols) + Your Party Resume (1 col)
 */
export default function GuestDashboardPage() {
  const navigate = useNavigate();
  const { profile, walletBalance } = useUser();
  const { logout } = useAuth();

  const [inviteCode, setInviteCode] = useState('');
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState(null);
  const [applyError, setApplyError] = useState(null);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await applicationService.getMyApplications();
      if (result.success) {
        setApplications(result.data || []);
      } else {
        setError(result.error || 'Failed to load applications');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Handle invite code submission
  const handleApply = async () => {
    if (!inviteCode.trim()) return;

    setIsApplying(true);
    setApplyError(null);

    try {
      // Extract code from URL or use as-is
      let code = inviteCode.trim();

      // Handle full URLs like https://partyresume.com/events/ABC123 or /e/ABC123
      const urlMatch = code.match(/\/(?:events?|e|invite)\/([^\/\?\s]+)/);
      if (urlMatch) {
        code = urlMatch[1];
      }

      // Validate the invite code exists
      const eventResult = await eventService.getEventByCode(code);

      if (!eventResult.success) {
        setApplyError(eventResult.error || 'Invalid invite code');
        setIsApplying(false);
        return;
      }

      // Navigate to event apply page using the invite code
      navigate(`/events/${code}`);
    } catch (err) {
      setApplyError('Failed to find event. Please check the code and try again.');
    } finally {
      setIsApplying(false);
    }
  };

  const handleModeChange = (mode) => {
    if (mode === 'host') {
      navigate('/host');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Calculate stats
  const hostedCount = profile?.eventsHostedCount || 0;
  const attendedCount = applications.filter((a) => a.status === 'accepted' || a.status === 'paid').length;
  const friendsCount = profile?.friendsCount || 0;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    return `${month} ${day}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'invited':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar
        mode="guest"
        onModeChange={handleModeChange}
        user={profile}
        walletBalance={walletBalance}
        onLogout={handleLogout}
        onLogoClick={() => navigate('/')}
      />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-mono text-3xl md:text-4xl font-bold uppercase tracking-wider text-black mb-1">
            FIND YOUR PARTY.
          </h1>
          <p className="font-mono text-lg text-gray-600">
            Where You belong
          </p>
        </div>

        {/* Invite code input */}
        <div className="border-2 border-black bg-white p-4 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Paste invite link or code..."
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleApply();
                  }
                }}
              />
            </div>
            <Button
              variant="primary"
              onClick={handleApply}
              disabled={!inviteCode.trim() || isApplying}
            >
              {isApplying ? 'SEARCHING...' : 'APPLY'}
            </Button>
          </div>

          {applyError && (
            <p className="mt-3 font-mono text-xs text-red-600 uppercase tracking-wider">
              {applyError}
            </p>
          )}
        </div>

        {/* Three-column layout */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Your Events Card - spans 2 columns */}
          <div className="lg:col-span-2 border-2 border-black bg-white min-w-0 overflow-hidden">
            <div className="px-6 py-4 border-b-2 border-black">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                YOUR EVENTS
              </h2>
            </div>

            {isLoading ? (
              <div className="p-12">
                <LoadingSpinner label="Loading events..." />
              </div>
            ) : error ? (
              <ErrorState message={error} onRetry={fetchApplications} />
            ) : applications.length === 0 ? (
              <NoApplicationsState />
            ) : (
              <div className="divide-y divide-gray-200">
                {applications.map((app) => (
                  <div
                    key={app.id}
                    className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/events/${app.event?.inviteCode}`)}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-sm font-bold uppercase tracking-wider text-black truncate">
                          {app.event?.title || 'Event'}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          {app.event?.date && (
                            <span className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                              {formatDate(app.event.date)}
                            </span>
                          )}
                          {app.event?.location && app.status !== 'accepted' && app.status !== 'paid' && (
                            <span className="font-mono text-xs text-gray-400 uppercase tracking-wider truncate">
                              {app.event.location}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`
                          shrink-0 px-2 py-0.5
                          font-mono text-[10px] font-semibold uppercase tracking-wider
                          ${getStatusColor(app.status)}
                        `}
                      >
                        {app.status}
                      </span>
                    </div>
                    {(app.status === 'accepted' || app.status === 'paid') && app.event?.location && (
                      <div className="mt-2 px-3 py-2 bg-green-50 border border-green-200">
                        <p className="font-mono text-xs text-green-800 uppercase tracking-wider break-words">
                          Location: {app.event.location}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Your Party Resume Card */}
          <div className="border-2 border-black bg-white">
            <div className="px-6 py-4 border-b-2 border-black">
              <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black">
                YOUR PARTY RESUME
              </h2>
            </div>

            <div className="p-8 flex flex-col items-center">
              {/* Avatar */}
              <div className="w-32 h-32 mb-6 border-2 border-black overflow-hidden flex items-center justify-center bg-gray-100">
                {profile?.avatarUrl ? (
                  <img
                    src={profile.avatarUrl}
                    alt={profile.displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <svg
                    className="w-16 h-16 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="square"
                      strokeLinejoin="miter"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                )}
              </div>

              {/* Name */}
              <h3 className="font-mono text-xl font-bold uppercase tracking-wider text-black text-center mb-8">
                {profile?.displayName || 'YOUR NAME'}
              </h3>

              {/* Stats */}
              <div className="w-full border-t-2 border-black pt-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="font-mono text-3xl font-bold text-black">
                      {hostedCount}
                    </p>
                    <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                      Hosted
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-3xl font-bold text-black">
                      {attendedCount}
                    </p>
                    <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                      Attended
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-3xl font-bold text-black">
                      {friendsCount}
                    </p>
                    <p className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                      Friends
                    </p>
                  </div>
                </div>
              </div>

              {/* View / Edit buttons */}
              <div className="w-full mt-8 flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => navigate(`/users/${profile?.userId}`)}
                >
                  VIEW
                </Button>
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => navigate('/credential-builder')}
                >
                  EDIT
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
